import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Analytics } from '@vercel/analytics/react'
import { AuthProvider, useAuth, SubscriptionProvider } from '@talktoast/shared'
import Layout from './components/Layout'
import Home from './components/Home'
import DevPanel from './components/DevPanel'
import DevFab from './components/DevFab'
import AuthScreen from './components/auth/AuthScreen'
import AuthCallback from './components/auth/AuthCallback'
import Playground from './components/Playground'
import PreferencesPage from './pages/PreferencesPage'
import { LandingPage, AboutPage, BetaSignUpPage, BetaConfirmationPage, PrivacyPolicyPage, TermsOfServicePage } from './pages/marketing'
import { MaintenanceMode } from './components/MaintenanceMode'

// Create a client instance outside component to avoid recreating on every render
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 2,
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
  },
})

// Wrapper component to connect Layout and Home
function LayoutWithHome() {
  const homeRef = useRef<any>()
  const [currentView, setCurrentView] = useState('home')
  const navigate = useNavigate()

  const handleHeaderNavigate = (path: string) => {
    if (path === '/settings') {
      homeRef.current?.setView?.('settings')
      setCurrentView('settings')
    } else if (path === '/settings/preferences') {
      homeRef.current?.setView?.('preferences')
      setCurrentView('preferences')
    } else if (path === '/settings/subscription') {
      // Subscription management is embedded in Home view state
      homeRef.current?.setView?.('subscription-management')
      setCurrentView('subscription-management')
    }
  }

  const handleBottomNavNavigate = (view: 'home' | 'calendar' | 'history') => {
    // Map bottom nav views to Home's currentView values
    const viewMap: Record<string, string> = {
      home: 'home',
      calendar: 'calendar',
      history: 'recipe-history'
    }

    const targetView = viewMap[view] || 'home'
    homeRef.current?.setView?.(targetView)
    setCurrentView(targetView)
  }

  const handlePlanClick = () => {
    // Navigate to session preferences to start new meal planning
    homeRef.current?.setView?.('session')
    setCurrentView('session')
  }

  const handleResumeSession = (sessionId: string, status: string) => {
    // Resume existing session by calling Home's resumeSession method
    console.log('[App] Resuming session:', sessionId, status)
    homeRef.current?.resumeSession?.(sessionId, status)

    // Update activeView to match the resumed session status
    const viewMap: Record<string, string> = {
      'planning': 'planning',
      'shopping': 'shopping',
      'scheduling': 'calendar'
    }
    const targetView = viewMap[status] || 'planning'
    setCurrentView(targetView)
  }

  return (
    <Layout
      onHeaderNavigate={handleHeaderNavigate}
      onBottomNavNavigate={handleBottomNavNavigate}
      onPlanClick={handlePlanClick}
      onResumeSession={handleResumeSession}
      activeView={currentView}
    >
      <Home ref={homeRef} />
    </Layout>
  )
}

// Auth Guard Component - handles auth routing logic
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { loading, isAuthenticated, isGuest } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-2">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  if (!isAuthenticated && !isGuest) {
    return <AuthScreen />
  }

  return <>{children}</>
}

function App() {
  // Hooks must be called unconditionally at the top
  const [showDevPanel, setShowDevPanel] = useState(false)

  // Check for maintenance mode
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true'

  // Only enable dev panel in development mode
  const isDevMode = import.meta.env.MODE === 'development'

  // Check for dev panel URL parameter and keyboard shortcut
  useEffect(() => {
    // Dev panel only available in development mode
    if (!isDevMode) return

    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('dev') === 'true') {
      setShowDevPanel(true)
    }

    // Keyboard shortcut: Ctrl+Shift+D (or Cmd+Shift+D on Mac)
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault()
        setShowDevPanel(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDevMode])

  // Show maintenance page if enabled (after all hooks)
  if (isMaintenanceMode) {
    return <MaintenanceMode />
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <Router>
            <Routes>
              {/* Public marketing routes - NO AuthGuard */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/beta-signup" element={<BetaSignUpPage />} />
              <Route path="/beta-confirmation" element={<BetaConfirmationPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />

              {/* PKCE email-confirmation callback — must be outside AuthGuard */}
              <Route path="/auth/callback" element={<AuthCallback />} />

              {/* Protected app routes - WITH AuthGuard */}
              <Route path="/app/*" element={<AuthGuard><LayoutWithHome /></AuthGuard>} />

              {/* Dev-only routes (full screen, no Layout) */}
              {isDevMode && <Route path="/app/playground" element={<AuthGuard><Playground /></AuthGuard>} />}
              {isDevMode && <Route path="/app/preferences" element={<AuthGuard><PreferencesPage onBack={() => window.history.back()} /></AuthGuard>} />}
            </Routes>

          {/* Dev Panel - only in development mode */}
          {isDevMode && (
            <>
              <DevPanel
                isVisible={showDevPanel}
                onClose={() => setShowDevPanel(false)}
              />

              {/* Dev Panel indicator when hidden */}
              {!showDevPanel && (
                <div className="fixed bottom-4 right-4 z-40">
                  <button
                    onClick={() => setShowDevPanel(true)}
                    className="bg-gray-800 text-white px-3 py-2 rounded-full text-sm font-mono hover:bg-gray-700 opacity-80"
                    title="Open Dev Panel (Ctrl+Shift+D)"
                  >
                    🛠️ DEV
                  </button>
                </div>
              )}
            </>
          )}

          {/* Dev FAB - available in all environments for authorized users */}
          <DevFab />
          </Router>
        </SubscriptionProvider>
      </AuthProvider>

      {/* React Query Devtools - only visible in development */}
      <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />

      {/* Vercel Analytics - tracks page views and custom events */}
      <Analytics />
    </QueryClientProvider>
  )
}

export default App